import React from 'react';
import { ReturnStatusKey, ClaimStatusKey, PaymentInfo, PaymentLink } from 'typings/returns';
import { useTrackEvent } from '@olxeu-a2b/delivery-utils/src/tracking/useNinja';
import ReturnsInfo from './ReturnsInfo';
import { useI18n } from '../../../../i18n';
import { USER_TYPE } from '../../constants';
import trackingKeys from '../../../../commons/constants/trackingKeys';

import S from './style/ReturnsActionInfoBlock';
import HowSafeDealWorksInfo from '../HowSafeDealWorksModal/HowSafeDealWorksInfo';

interface ReturnsActionInfoBlockProps {
  isDesktop: boolean;
  isSeller: boolean;
  adId: string | number;
  status: ReturnStatusKey | ClaimStatusKey;
  onFillDataClick: (returnId: string, userType: USER_TYPE) => void;
  shouldShowPaymentBtn: boolean;
  refetch: PaymentInfo;
  sellerId: string;
  returnId?: string;
}

const { confirmDetails, redoPayment } = trackingKeys.events.safeDeal;

const { returnOverview, returnDetails } = trackingKeys.touchPointPage.returns;

const ReturnsActionInfoBlock: React.FC<ReturnsActionInfoBlockProps> = ({
  isDesktop,
  isSeller,
  returnId,
  adId,
  status,
  sellerId,
  onFillDataClick,
  shouldShowPaymentBtn,
  refetch,
}) => {
  const i18n = useI18n();
  const trackEvent = useTrackEvent();

  const showConfirmDataBtn =
    status === 'SELLER_BUYER_RESPONDED' || status === 'BUYER_AWAITING_RESPONSES';
  const userType = isSeller ? USER_TYPE.SELLER : USER_TYPE.BUYER;
  const touchPointPage = window.location.pathname.includes(returnId)
    ? returnDetails
    : returnOverview;

  const trackingExtras = {
    seller_type: isSeller ? USER_TYPE.SELLER : USER_TYPE.BUYER,
    ad_id: adId,
    seller_id: sellerId,
    touch_point_page: touchPointPage,
  };

  const onConfirmDataHandler = () => {
    trackEvent(confirmDetails, null, null, trackingExtras);
    onFillDataClick(returnId, userType);
  };

  const onRedoPaymentHandler = async () => {
    trackEvent(redoPayment, null, null, trackingExtras);
    await refetch().then((response: { data: PaymentLink }) => {
      const paymentLink = response.data.payment;
      window.location.href = paymentLink;
    });
  };

  const showSafeDealModal = () => (
    <HowSafeDealWorksInfo
      isDesktop={isDesktop}
      trackingExtras={trackingExtras}
      userType={userType}
    />
  );

  return (
    <S.ReturnsActionInfoBlock
      data-testid="returns-action-info-block"
      data-cy="returns-action-info-block"
    >
      {returnId ? (
        <ReturnsInfo
          isDesktop={isDesktop}
          isSeller={isSeller}
          adId={adId}
          sellerId={sellerId}
          touchPointPage={touchPointPage}
        />
      ) : (
        showSafeDealModal()
      )}
      {showConfirmDataBtn && (
        <S.ActionButton data-cy="returns-confirm-data-btn" onClick={onConfirmDataHandler}>
          {i18n('dlv_returns_confirm_data')}
        </S.ActionButton>
      )}
      {shouldShowPaymentBtn && (
        <S.ActionButton data-cy="returns-redo-payment-btn" onClick={onRedoPaymentHandler}>
          {i18n('dlv_returns_redo_payment')}
        </S.ActionButton>
      )}
    </S.ReturnsActionInfoBlock>
  );
};

export default ReturnsActionInfoBlock;
